import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import BlogLayout from '../blog/BlogLayout';
import MetaTags from '../common/MetaTags';
import { InstantSearch, SearchBox, Hits, Configure, useInstantSearch } from 'react-instantsearch';
import { searchClient, HELP_ARTICLES_INDEX, isAlgoliaConfigured } from '../../services/algoliaClient';

// Component to handle search UI state - must be inside InstantSearch context
function SearchResults({ Hit }) {
  const { indexUiState, results } = useInstantSearch();
  const query = indexUiState.query || '';

  if (!query) {
    return null;
  }

  const hasResults = results && results.hits && results.hits.length > 0;

  return (
    <>
      <div className="search-results-section">
        <div className="search-results-container">
          <div className="search-results-header">
            {hasResults ? `${results.nbHits} ${results.nbHits === 1 ? 'result' : 'results'} found` : 'No results found'}
          </div>
          {hasResults ? (
            <Hits hitComponent={Hit} />
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '32px 20px',
              color: 'var(--text-secondary)'
            }}>
              <p style={{ fontSize: '14px', margin: '0 0 6px 0' }}>
                No articles match your search.
              </p>
              <p style={{ fontSize: '13px', margin: 0 }}>
                Try different keywords or browse categories below.
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="search-divider">
        <span>or browse by category</span>
      </div>
    </>
  );
}

export default function HelpView() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadHelpArticles();
  }, []);

  async function loadHelpArticles() {
    try {
      setLoading(true);

      // Load all published help articles (no orderBy to avoid index requirement)
      const snapshot = await getDocs(collection(db, 'help_articles'));
      const loadedArticles = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(article => article.published === true)
        .sort((a, b) => (a.order || 0) - (b.order || 0)); // Sort manually by order

      setArticles(loadedArticles);

      // Group articles by category
      const cats = {};
      loadedArticles.forEach(article => {
        const cat = article.category || 'General';
        if (!cats[cat]) {
          cats[cat] = [];
        }
        cats[cat].push(article);
      });
      setCategories(Object.keys(cats).map(name => ({ name, articles: cats[name] })));

      setLoading(false);
    } catch (error) {
      console.error('Error loading help articles:', error);
      setLoading(false);
    }
  }

  // Custom Hit component for search results
  const Hit = ({ hit }) => (
    <a
      href={`/help/${hit.slug}`}
      style={{
        display: 'block',
        padding: '32px 24px',
        background: 'white',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        textDecoration: 'none',
        color: 'var(--text)',
        transition: 'all 0.3s ease',
        height: '100%'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.borderColor = 'var(--primary)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
    >
      <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0', color: 'var(--text)' }}>
        {hit.title}
      </h3>
      {hit.description && (
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 12px 0', lineHeight: '1.6' }}>
          {hit.description}
        </p>
      )}
      {hit.category && (
        <span style={{
          display: 'inline-block',
          fontSize: '12px',
          color: 'var(--text-secondary)',
          padding: '4px 12px',
          background: 'var(--bg)',
          borderRadius: '4px',
          fontWeight: '500'
        }}>
          {hit.category}
        </span>
      )}
    </a>
  );

  return (
    <>
      <MetaTags
        title="Help Center"
        description="Find answers and learn how to use VBallStat to track your volleyball performance and improve your game."
        type="website"
      />

      <style>{`
        .help-view-wrapper {
          max-width: 100%;
          margin: 0 auto;
          padding: 0;
        }

        .help-view-header {
          text-align: center;
          padding: 80px 20px 60px;
          margin-bottom: 0;
        }

        .help-view-title {
          font-size: 48px;
          font-weight: 700;
          margin-bottom: 16px;
          color: var(--text);
          line-height: 1.2;
        }

        .help-view-subtitle {
          font-size: 18px;
          color: var(--text-secondary);
          line-height: 1.6;
          max-width: 600px;
          margin: 0 auto 40px;
        }

        .help-search-container {
          max-width: 680px;
          margin: 0 auto;
          position: relative;
        }

        .help-search-input {
          width: 100%;
          padding: 18px 24px 18px 56px;
          font-size: 16px;
          border: none;
          border-radius: 8px;
          outline: none;
          background: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          transition: box-shadow 0.2s;
        }

        .help-search-input:focus {
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
        }

        .help-search-icon {
          position: absolute;
          left: 22px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 20px;
          color: #9b9a97;
        }

        .help-content-wrapper {
          max-width: 1600px;
          margin: 0 auto;
          padding: 0 40px 60px;
        }

        .help-categories-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-bottom: 80px;
        }

        .help-category-card {
          background: white;
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 32px 28px;
          text-decoration: none;
          color: inherit;
          display: block;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .help-category-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
          border-color: var(--primary);
        }

        .help-category-icon {
          font-size: 56px;
          margin-bottom: 20px;
          display: block;
        }

        .help-category-name {
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 8px;
          color: var(--text);
        }

        .help-category-count {
          font-size: 14px;
          color: var(--text-secondary);
          margin-bottom: 20px;
        }

        .help-articles-section {
          margin-top: 40px;
        }

        .help-section-title {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 20px;
          color: #1a1a1a;
        }

        .help-article-link {
          display: block;
          padding: 16px 0;
          border-bottom: 1px solid #e5e7eb;
          text-decoration: none;
          color: #667eea;
          font-size: 16px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .help-article-link:hover {
          color: #764ba2;
          padding-left: 8px;
        }

        .help-article-link:last-child {
          border-bottom: none;
        }

        /* Algolia Search Styles */
        .ais-SearchBox {
          width: 100%;
        }

        .ais-SearchBox-form {
          position: relative;
        }

        .ais-SearchBox-input {
          width: 100%;
          padding: 18px 24px 18px 56px !important;
          font-size: 16px;
          border: none !important;
          border-radius: 8px !important;
          outline: none;
          background: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
          transition: box-shadow 0.2s;
        }

        .ais-SearchBox-submit, .ais-SearchBox-reset {
          display: none;
        }

        .ais-SearchBox-input:focus {
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2) !important;
        }

        .help-search-container .help-search-icon {
          z-index: 10;
          pointer-events: none;
        }

        .ais-Hits-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 24px;
        }

        .ais-Hits-item {
          list-style: none;
          margin: 0;
        }

        .search-results-section {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 20px 60px;
        }

        .search-results-container {
          background: transparent;
        }

        .search-results-header {
          font-size: 24px;
          font-weight: 700;
          color: var(--text);
          margin-top: -50px;
          margin-bottom: 24px;
          text-align: center;
        }

        .search-divider {
          text-align: center;
          margin: 40px 0;
          position: relative;
        }

        .search-divider::before {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          top: 50%;
          height: 1px;
          background: var(--border);
        }

        .search-divider span {
          position: relative;
          background: white;
          padding: 0 20px;
          color: var(--text-secondary);
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 600;
        }

        .categories-section-title {
          font-size: 24px;
          font-weight: 700;
          color: var(--text);
          margin-bottom: 24px;
          text-align: center;
        }

        @media (max-width: 1200px) {
          .help-categories-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 900px) {
          .help-categories-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .help-view-header {
            padding: 60px 20px 50px;
          }

          .help-view-title {
            font-size: 32px;
          }

          .help-view-subtitle {
            font-size: 16px;
          }

          .help-content-wrapper {
            padding: 40px 16px;
          }

          .help-categories-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }

          .help-category-card {
            padding: 32px 24px;
          }

          .help-category-icon {
            font-size: 48px;
          }

          .help-category-name {
            font-size: 20px;
          }

          .search-results-section {
            padding: 32px 16px;
          }

          .search-divider {
            margin: 40px 0;
          }

          .categories-section-title {
            font-size: 26px;
          }
        }
      `}</style>

      <BlogLayout showSidebar={false} whiteBackground={false}>
        <div className="help-view-wrapper">
          {isAlgoliaConfigured() && searchClient ? (
            <InstantSearch searchClient={searchClient} indexName={HELP_ARTICLES_INDEX}>
              {/* Hero Section */}
              <div className="help-view-header">
                <h1 className="help-view-title">How can we help you?</h1>
                <p className="help-view-subtitle">
                  Learn about VBallStat features and find answers to your questions
                </p>

                {/* Search in Hero */}
                <div className="help-search-container">
                  <span className="help-search-icon">🔍</span>
                  <SearchBox
                    placeholder="Search for help articles..."
                    autoFocus={false}
                  />
                </div>
              </div>

              <Configure hitsPerPage={20} filters="published:true" />

              {/* Search Results */}
              <SearchResults Hit={Hit} />

              <div className="help-content-wrapper">
            {/* Loading State */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '14px', color: '#9b9a97' }}>
                  Loading help articles...
                </div>
              </div>
            )}

            {/* No Articles */}
            {!loading && articles.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: '18px', color: '#9b9a97', marginBottom: '8px' }}>
                No help articles yet
              </div>
              <div style={{ fontSize: '14px', color: '#9b9a97' }}>
                Check back soon for helpful guides and tutorials!
              </div>
            </div>
          )}

          {/* Categories as Cards */}
          {!loading && categories.length > 0 && (
            <div>
              <h2 className="categories-section-title">Browse Categories</h2>
              <div className="help-categories-grid">
            {categories.map(category => (
              <div key={category.name} className="help-category-card">
                <h2 className="help-category-name">{category.name}</h2>
                <p className="help-category-count">
                  {category.articles.length} {category.articles.length === 1 ? 'article' : 'articles'}
                </p>
                <div style={{ marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '20px' }}>
                  {category.articles.slice(0, 5).map(article => (
                    <a
                      key={article.id}
                      href={`/help/${article.slug}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 0',
                        color: 'var(--primary)',
                        textDecoration: 'none',
                        fontSize: '13px',
                        transition: 'all 0.2s',
                        fontWeight: '500'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = 'var(--primary-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = 'var(--primary)';
                      }}
                    >
                      <span style={{ marginRight: '8px' }}>→</span>
                      {article.title}
                    </a>
                  ))}
                  {category.articles.length > 5 && (
                    <a
                      href={`/help/category/${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                      style={{
                        fontSize: '13px',
                        color: 'var(--primary)',
                        marginTop: '8px',
                        fontWeight: '600',
                        textDecoration: 'none',
                        display: 'inline-block',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = 'var(--primary-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = 'var(--primary)';
                      }}
                    >
                      +{category.articles.length - 5} more →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

                {/* CTA Section */}
                {!loading && articles.length > 0 && (
                  <div style={{
                    marginTop: '100px',
                    padding: '60px 40px',
                    background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
                  }}>
                    <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'white' }}>
                      Still need help?
                    </h2>
                    <p style={{ fontSize: '17px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '32px', maxWidth: '500px', margin: '0 auto 32px' }}>
                      Can't find what you're looking for? Get in touch with us and we'll be happy to help.
                    </p>
                    <a
                      href="/contact-us"
                      style={{
                        display: 'inline-block',
                        padding: '14px 40px',
                        background: 'white',
                        color: 'var(--primary)',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: '700',
                        fontSize: '16px',
                        transition: 'all 0.2s',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }}
                    >
                      Contact Support
                    </a>
                  </div>
                )}
              </div>
            </InstantSearch>
          ) : (
            <>
              {/* Hero Section - No Search */}
              <div className="help-view-header">
                <h1 className="help-view-title">How can we help you?</h1>
                <p className="help-view-subtitle">
                  Learn about VBallStat features and find answers to your questions
                </p>

                {/* Disabled Search */}
                <div className="help-search-container">
                  <span className="help-search-icon">🔍</span>
                  <input
                    type="text"
                    className="help-search-input"
                    placeholder="Search for help articles..."
                    disabled
                  />
                </div>
              </div>

              <div className="help-content-wrapper">
                {/* Loading State */}
                {loading && (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <div style={{ fontSize: '14px', color: '#9b9a97' }}>
                      Loading help articles...
                    </div>
                  </div>
                )}

                {/* No Articles */}
                {!loading && articles.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <div style={{ fontSize: '18px', color: '#9b9a97', marginBottom: '8px' }}>
                      No help articles yet
                    </div>
                    <div style={{ fontSize: '14px', color: '#9b9a97' }}>
                      Check back soon for helpful guides and tutorials!
                    </div>
                  </div>
                )}

                {/* Categories as Cards */}
                {!loading && categories.length > 0 && (
                  <div>
                    <h2 className="categories-section-title">Browse Categories</h2>
                    <div className="help-categories-grid">
                      {categories.map(category => (
                        <div key={category.name} className="help-category-card">
                          <h2 className="help-category-name">{category.name}</h2>
                          <p className="help-category-count">
                            {category.articles.length} {category.articles.length === 1 ? 'article' : 'articles'}
                          </p>
                          <div style={{ marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '20px' }}>
                            {category.articles.slice(0, 5).map(article => (
                              <a
                                key={article.id}
                                href={`/help/${article.slug}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '10px 0',
                                  color: 'var(--primary)',
                                  textDecoration: 'none',
                                  fontSize: '13px',
                                  transition: 'all 0.2s',
                                  fontWeight: '500'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.color = 'var(--primary-hover)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.color = 'var(--primary)';
                                }}
                              >
                                <span style={{ marginRight: '8px' }}>→</span>
                                {article.title}
                              </a>
                            ))}
                            {category.articles.length > 5 && (
                              <a
                                href={`/help/category/${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                                style={{
                                  fontSize: '13px',
                                  color: 'var(--primary)',
                                  marginTop: '8px',
                                  fontWeight: '600',
                                  textDecoration: 'none',
                                  display: 'inline-block',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.color = 'var(--primary-hover)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.color = 'var(--primary)';
                                }}
                              >
                                +{category.articles.length - 5} more →
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA Section */}
                {!loading && articles.length > 0 && (
                  <div style={{
                    marginTop: '100px',
                    padding: '60px 40px',
                    background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
                  }}>
                    <h2 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: 'white' }}>
                      Still need help?
                    </h2>
                    <p style={{ fontSize: '17px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '32px', maxWidth: '500px', margin: '0 auto 32px' }}>
                      Can't find what you're looking for? Get in touch with us and we'll be happy to help.
                    </p>
                    <a
                      href="/contact-us"
                      style={{
                        display: 'inline-block',
                        padding: '14px 40px',
                        background: 'white',
                        color: 'var(--primary)',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: '700',
                        fontSize: '16px',
                        transition: 'all 0.2s',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }}
                    >
                      Contact Support
                    </a>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </BlogLayout>
    </>
  );
}
